import React, { useState, useEffect } from 'react'
import { Card, CardBody, CardHeader } from '../../../../_metronic/_partials/controls'
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import { getCuponById, updateCupon, postCupon } from '../../../../api/cupon'
import { getMarcas } from '../../../../api/marca'
import { useSkeleton } from '../../../hooks/useSkeleton'
import { alertError, alertSuccess } from '../../../../utils/logger'

const CUPON_TYPES = [
  { value: 'percentage', label: '% Descuento' },
  { value: 'fixed_amount', label: '€ Descuento' },
  { value: 'free_shipping', label: 'Envío Gratis' }
]

const SHIPPING_ZONES = [
  { value: 'spain', label: 'España' },
  { value: 'europe', label: 'Europa' },
  { value: 'all', label: 'Todos' }
]

function getEmptyCupon() {
  const today = new Date()
  const nextMonth = new Date(today)
  nextMonth.setMonth(nextMonth.getMonth() + 1)
  
  return {
    code: '',
    name: '',
    type: 'percentage',
    discountPercentage: 10,
    maxDiscount: 0,
    discountAmount: 0,
    minimumPurchase: 0,
    shippingZone: 'all',
    startDate: today.toISOString().split('T')[0],
    endDate: nextMonth.toISOString().split('T')[0],
    maxUses: 0,
    usedCount: 0,
    brand: null,
    active: true
  }
}

export default function EditCuponPage() {
  const [cupon, setCupon] = useState(getEmptyCupon())
  const [marcas, setMarcas] = useState([])
  const cuponId = useParams().id
  const history = useHistory()

  const { isLoading: isLoadingData, disableLoading: disableLoadingData, ContentSkeleton } = useSkeleton()

  useEffect(() => {
    // Cargar marcas
    getMarcas().then(res => {
      if (res.status === 200) {
        setMarcas(res.data)
      }
    }).catch(error => {
      console.error('Error loading marcas:', error)
    })

    if (cuponId) {
      getCuponById(cuponId).then(res => {
        if (res.status === 200) {
          const data = res.data
          // Formatear fechas para inputs
          if (data.startDate) {
            data.startDate = new Date(data.startDate).toISOString().split('T')[0]
          }
          if (data.endDate) {
            data.endDate = new Date(data.endDate).toISOString().split('T')[0]
          }
          // Si brand es un objeto, extraer el ID
          if (data.brand && typeof data.brand === 'object') {
            data.brand = data.brand._id
          }
          setCupon(data)
          disableLoadingData()
        }
      }).catch(error => {
        alertError({ error: error, customMessage: 'No se pudo obtener el cupón.' })
        history.push('/cupones')
      })
    } else {
      disableLoadingData()
    }
  }, [cuponId, disableLoadingData, history])

  function saveCupon() {
    // Validaciones básicas
    if (!cupon.code || cupon.code.trim() === '') {
      alertError({ customMessage: 'El código del cupón es obligatorio.' })
      return
    }
    if (!cupon.name || cupon.name.trim() === '') {
      alertError({ customMessage: 'El nombre del cupón es obligatorio.' })
      return
    }
    if (!cupon.startDate || !cupon.endDate) {
      alertError({ customMessage: 'Las fechas de inicio y fin son obligatorias.' })
      return
    }
    if (new Date(cupon.startDate) > new Date(cupon.endDate)) {
      alertError({ customMessage: 'La fecha de inicio no puede ser posterior a la fecha de fin.' })
      return
    }

    // Preparar datos según el tipo
    const dataToSave = { ...cupon }
    
    // Limpiar campos no relevantes según el tipo
    if (cupon.type === 'percentage') {
      dataToSave.discountAmount = 0
      dataToSave.minimumPurchase = 0
      dataToSave.shippingZone = 'all'
    } else if (cupon.type === 'fixed_amount') {
      dataToSave.discountPercentage = 0
      dataToSave.maxDiscount = 0
      dataToSave.shippingZone = 'all'
    } else if (cupon.type === 'free_shipping') {
      dataToSave.discountPercentage = 0
      dataToSave.maxDiscount = 0
      dataToSave.discountAmount = 0
      dataToSave.minimumPurchase = 0
    }

    // Si brand es "all" o vacío, guardar como null
    if (!dataToSave.brand || dataToSave.brand === 'all') {
      dataToSave.brand = null
    }

    if (cuponId) {
      updateCupon(cuponId, dataToSave).then(res => {
        if (res.status === 200) {
          alertSuccess({ title: '¡Guardado!', customMessage: 'Cambios guardados correctamente.' })
          history.push('/cupones')
        }
      }).catch(error => {
        alertError({ error: error, customMessage: 'No se pudieron guardar los cambios.' })
      })
    } else {
      postCupon(dataToSave).then(res => {
        if (res.status === 200 || res.status === 201) {
          alertSuccess({ title: '¡Creado!', customMessage: 'Cupón creado correctamente.' })
          history.push('/cupones')
        }
      }).catch(error => {
        alertError({ error: error, customMessage: 'No se pudo crear el cupón.' })
      })
    }
  }

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value
    setCupon({ ...cupon, [field]: value })
  }

  const handleNumberChange = (field) => (event) => {
    const raw = event.target.value
    const num = raw === '' ? 0 : Number(raw)
    setCupon({ ...cupon, [field]: num })
  }

  if (isLoadingData)
    return <ContentSkeleton />
  
  return (
    <>
      <Card>
        <CardHeader title={cuponId ? 'Editar Cupón' : 'Crear Cupón'} />
        <CardBody>
          {/* Información básica */}
          <div className="row">
            <div className="col-md-4">
              <TextField
                id="code"
                label="Código del cupón"
                value={cupon.code || ''}
                onChange={handleChange('code')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                required
                fullWidth
                helperText="Ej: VERANO20, DESCUENTO10"
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </div>
            <div className="col-md-4">
              <TextField
                id="name"
                label="Nombre / Descripción"
                value={cupon.name || ''}
                onChange={handleChange('name')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                required
                fullWidth
                helperText="Descripción interna del cupón"
              />
            </div>
            <div className="col-md-4">
              <FormControl variant="outlined" margin="normal" fullWidth>
                <InputLabel shrink>Tipo de cupón</InputLabel>
                <Select
                  value={cupon.type || 'percentage'}
                  onChange={handleChange('type')}
                  label="Tipo de cupón"
                >
                  {CUPON_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          {/* Campos según el tipo */}
          <div className="row mt-3">
            {cupon.type === 'percentage' && (
              <>
                <div className="col-md-4">
                  <TextField
                    id="discountPercentage"
                    label="Porcentaje de descuento"
                    type="number"
                    value={cupon.discountPercentage || 0}
                    onChange={handleNumberChange('discountPercentage')}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 0, max: 100 }}
                    helperText="Valor entre 0 y 100"
                  />
                </div>
                <div className="col-md-4">
                  <TextField
                    id="maxDiscount"
                    label="Descuento máximo (€)"
                    type="number"
                    value={cupon.maxDiscount || 0}
                    onChange={handleNumberChange('maxDiscount')}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 0 }}
                    helperText="0 = sin límite"
                  />
                </div>
              </>
            )}

            {cupon.type === 'fixed_amount' && (
              <>
                <div className="col-md-4">
                  <TextField
                    id="discountAmount"
                    label="Cantidad de descuento (€)"
                    type="number"
                    value={cupon.discountAmount || 0}
                    onChange={handleNumberChange('discountAmount')}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 0 }}
                    helperText="Cantidad fija en euros"
                  />
                </div>
                <div className="col-md-4">
                  <TextField
                    id="minimumPurchase"
                    label="Compra mínima (€)"
                    type="number"
                    value={cupon.minimumPurchase || 0}
                    onChange={handleNumberChange('minimumPurchase')}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 0 }}
                    helperText="0 = sin mínimo"
                  />
                </div>
              </>
            )}

            {cupon.type === 'free_shipping' && (
              <div className="col-md-4">
                <FormControl variant="outlined" margin="normal" fullWidth>
                  <InputLabel shrink>Zona de envío gratis</InputLabel>
                  <Select
                    value={cupon.shippingZone || 'all'}
                    onChange={handleChange('shippingZone')}
                    label="Zona de envío gratis"
                  >
                    {SHIPPING_ZONES.map(zone => (
                      <MenuItem key={zone.value} value={zone.value}>{zone.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}
          </div>

          {/* Fechas y usos */}
          <div className="row mt-4">
            <div className="col-md-3">
              <TextField
                id="startDate"
                label="Fecha de inicio"
                type="date"
                value={cupon.startDate || ''}
                onChange={handleChange('startDate')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                required
                fullWidth
              />
            </div>
            <div className="col-md-3">
              <TextField
                id="endDate"
                label="Fecha de fin"
                type="date"
                value={cupon.endDate || ''}
                onChange={handleChange('endDate')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                required
                fullWidth
              />
            </div>
            <div className="col-md-3">
              <TextField
                id="maxUses"
                label="Usos máximos"
                type="number"
                value={cupon.maxUses || 0}
                onChange={handleNumberChange('maxUses')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                fullWidth
                inputProps={{ min: 0 }}
                helperText="0 = ilimitado"
              />
            </div>
            <div className="col-md-3">
              <TextField
                id="usedCount"
                label="Veces usado"
                type="number"
                value={cupon.usedCount || 0}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                fullWidth
                disabled
                helperText="Contador automático"
              />
            </div>
          </div>

          {/* Marca y estado */}
          <div className="row mt-4">
            <div className="col-md-4">
              <FormControl variant="outlined" margin="normal" fullWidth>
                <InputLabel shrink>Marca (opcional)</InputLabel>
                <Select
                  value={cupon.brand || 'all'}
                  onChange={handleChange('brand')}
                  label="Marca (opcional)"
                >
                  <MenuItem value="all">Todas las marcas</MenuItem>
                  {marcas.map(marca => (
                    <MenuItem key={marca._id} value={marca._id}>{marca.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>Aplica a productos de esta marca solamente</FormHelperText>
              </FormControl>
            </div>
            <div className="col-md-4 d-flex align-items-center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cupon.active || false}
                    onChange={handleChange('active')}
                    color="primary"
                  />
                }
                label="Cupón activo"
              />
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="mt-4">
        <Button
          onClick={() => history.push('/cupones')}
          variant="outlined"
          style={{ marginRight: '20px' }}
        >
          Volver
        </Button>
        <Button
          onClick={() => saveCupon()}
          variant="contained"
          color="primary"
        >
          {cuponId ? 'Guardar cambios' : 'Crear cupón'}
        </Button>
      </div>
    </>
  );
}

